<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { useRoute } from 'vue-router';
    import { ChevronDownIcon, ChevronUpIcon, SearchIcon } from '@lucide/vue';
    import EchoButton from '../EchoButton.vue';
    import EchoPopover from '../EchoPopover.vue';
    import TextInput, { TextInputWidth } from '../TextInput.vue';
    import DashboardNavigationList from './DashboardNavigationList.vue';
    import { useDashboardStore } from '@/js/stores/dashboard';
    import { __ } from '@/js/stores/expressions';

    const dashboard = useDashboardStore();
    const route = useRoute();

    const popoverRef = ref<InstanceType<typeof EchoPopover> | null>(null);
    const search = ref('');

    const activeId = computed(() => route.query.id?.toString());

    const favorites = computed(() =>
        dashboard.items
            .filter((item) => item.isFavorite)
            .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title))
    );
    const others = computed(() =>
        dashboard.items
            .filter((item) => !item.isFavorite)
            .sort((a, b) => a.title.localeCompare(b.title))
    );

    const hasSearch = computed(() => search.value.trim().length > 0);

    const matchesSearch = (title: string) => {
        if (!hasSearch.value) return true;
        return title.toLowerCase().includes(search.value.trim().toLowerCase());
    };

    const filteredFavorites = computed(() =>
        favorites.value.filter((item) => matchesSearch(item.title))
    );
    const filteredOthers = computed(() => others.value.filter((item) => matchesSearch(item.title)));

    const hasAnyResults = computed(
        () => filteredFavorites.value.length > 0 || filteredOthers.value.length > 0
    );

    const onSelect = () => {
        popoverRef.value?.close();
    };

    const onFavoriteToggle = async (id: string) => {
        try {
            await dashboard.toggleFavorite(id);
        } catch (error) {
            console.error('Failed to toggle favorite:', error);
        }
    };

    const onReorderFavorites = async (orderedIds: string[]) => {
        const nonFavIds = others.value.map((item) => item.id);
        try {
            await dashboard.reorderDashboards([...orderedIds, ...nonFavIds]);
        } catch (error) {
            console.error('Failed to reorder dashboards:', error);
        }
    };
</script>

<template>
    <EchoPopover ref="popoverRef" type="popover" position="bottom-end">
        <template #button="{ anchor, togglePopover, open, buttonAttrs }">
            <EchoButton :ref="anchor" variant="primary" v-bind="buttonAttrs" @click="togglePopover">
                {{ __('all-dashboards-with-count', { count: dashboard.items.length.toString() }) }}
                <template #icon-right>
                    <ChevronUpIcon v-if="open" />
                    <ChevronDownIcon v-else />
                </template>
            </EchoButton>
        </template>

        <template #popover>
            <div class="flex flex-col w-64 max-h-[min(24rem,70vh)]">
                <div class="border-b border-gray-lightest pb-4">
                    <TextInput
                        v-model="search"
                        :width="TextInputWidth.Full"
                        :placeholder="__('search-dashboards-placeholder')"
                        :aria-label="__('search-dashboards-placeholder')"
                    >
                        <template #end-adornment>
                            <SearchIcon class="size-4" />
                        </template>
                    </TextInput>
                </div>

                <div
                    class="flex flex-col gap-4 overflow-y-auto custom-scrollbar py-4 px-4 -mx-4 -mb-4 min-h-0"
                >
                    <section
                        v-if="
                            filteredFavorites.length > 0 || (favorites.length === 0 && !hasSearch)
                        "
                        class="flex flex-col gap-1 border-b border-gray-lightest pb-4"
                    >
                        <h3 class="text-body-bold text-gray-dark">{{ __('my-favorites') }}</h3>
                        <DashboardNavigationList
                            :dashboards="filteredFavorites"
                            group-name="favorites"
                            :active-id="activeId"
                            :empty-state="hasSearch ? undefined : __('no-favorites-yet')"
                            :sortable="!hasSearch"
                            @select="onSelect"
                            @favorite-toggle="onFavoriteToggle"
                            @reorder="onReorderFavorites"
                        />
                    </section>

                    <section v-if="filteredOthers.length > 0" class="flex flex-col gap-1">
                        <h3 class="text-body-bold text-gray-dark">{{ __('all-dashboards') }}</h3>
                        <DashboardNavigationList
                            :dashboards="filteredOthers"
                            group-name="others"
                            :active-id="activeId"
                            @select="onSelect"
                            @favorite-toggle="onFavoriteToggle"
                        />
                    </section>

                    <p v-if="!hasAnyResults && hasSearch" class="text-gray-medium">
                        {{ __('no-dashboards-found') }}
                    </p>
                </div>
            </div>
        </template>
    </EchoPopover>
</template>
